import { useTranslations } from '@vocab/react';
import {
  Box,
  Text,
  ButtonLink,
  Columns,
  Column,
  Hidden,
  Button,
  IconClear,
} from 'braid-design-system';
import { useEffect, useState } from 'react';

import { useGetAppLink } from 'src/core/utils/appLinkUtil';
import { useAnalytics } from 'src/provider/analytics';
import { usePage } from 'src/provider/page';

import translations from './.vocab';

import * as styles from './MobileAppBanner.css';

interface ScrollStickyModalProps {
  scrollThreshold?: number;
}

const useVisibleBannerWhenScroll = (
  scrollThreshold: number,
  isCloseBanner: boolean,
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > scrollThreshold && !isCloseBanner) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrollThreshold, isCloseBanner, setIsVisible]);
};

export default ({ scrollThreshold = 300 }: ScrollStickyModalProps) => {
  const { t } = useTranslations(translations);
  const { trackAnalyticsInteraction } = useAnalytics();
  const {
    pageState: { pageName, threadId },
  } = usePage();

  const appLink = useGetAppLink();

  const [isVisible, setIsVisible] = useState(false);
  const [isCloseBanner, setIsCloseBanner] = useState(false);

  useVisibleBannerWhenScroll(scrollThreshold, isCloseBanner, setIsVisible);
  const onCloseClick = () => {
    setIsCloseBanner(true);
    setIsVisible(false);
  };

  if (!isVisible) return null;

  if (pageName !== 'CommunityThreadDetails') return null;

  return (
    <Hidden above="mobile">
      <Box className={styles.container}>
        <Columns
          space={{
            mobile: 'small',
            tablet: 'medium',
            desktop: 'large',
            wide: 'xlarge',
          }}
          alignY="center"
        >
          <Column>
            <Box display="flex" alignItems="center">
              <Button
                icon={<IconClear />}
                variant="transparent"
                id="mobile-close-app-banner"
                onClick={onCloseClick}
              />
              <Text
                size="small"
                weight="medium"
                align={{
                  mobile: 'left',
                  tablet: 'left',
                }}
              >
                {t('viewThread')}
              </Text>
            </Box>
          </Column>
          <Column width="1/3">
            <ButtonLink
              id="mobile-open-app-banner-btn"
              tone="brandAccent"
              variant="solid"
              href={appLink}
              target="_blank"
              onClick={() =>
                trackAnalyticsInteraction('AppBannerClick', {
                  threadId,
                  nudgeType: t('nudgeType'),
                  message: t('nudgeMessage'),
                  linkText: t('linkText'),
                })
              }
            >
              {t('openAppMobile')}
            </ButtonLink>
          </Column>
        </Columns>
      </Box>
    </Hidden>
  );
};
